import { uniq } from 'es-toolkit';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, DialogContentText } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { Dialog } from '@paragon/popup-state';
import { getRanges, notNullish } from '@paragon/utilities';

import {
  removeProductionComponentInstancesFromBatch,
  removeProductionComponentInstancesFromBundle,
  removeProductionComponentInstancesFromProductionGroup,
} from '../../../actions/productionComponentInstances';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabByAnalysisSetGuid } from '../../../selectors/productionComponentInstances';
import { ProductionGroupGridRow } from '../../../selectors/productionGroupGrid';
import { useAppDispatch, useAppSelector } from '../../hooks';

interface ProductionGroupGridRowActionsProps {
  row: ProductionGroupGridRow;
}

export function ComponentsToBatchOrBundleGridRowActions(props: ProductionGroupGridRowActionsProps) {
  const dispatch = useAppDispatch();
  const productionComponentInstancesByAnalysisSetGuid = useAppSelector(
    getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabByAnalysisSetGuid,
  );
  const batches = useAppSelector((state) => state.batches);
  const bundles = useAppSelector((state) => state.bundles);
  const {
    row: { analysisSetGuid },
  } = props;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const productionComponentInstancesForAnalysisSetGuid =
    productionComponentInstancesByAnalysisSetGuid[analysisSetGuid] ?? [];
  const productionComponentInstanceGuidsForAnalysisSetGuid = productionComponentInstancesForAnalysisSetGuid.map(
    (instance) => instance.guid,
  );
  const batchGuids = uniq(productionComponentInstancesForAnalysisSetGuid.map((instance) => instance.batchGuid)).filter(
    notNullish,
  );
  const bundleGuids = uniq(
    productionComponentInstancesForAnalysisSetGuid.map((instance) => instance.bundleGuid),
  ).filter(notNullish);

  const isPartOfBatchesOrBundles = batchGuids.length > 0 || bundleGuids.length > 0;
  const batchesString =
    batchGuids.length > 0
      ? `batch${batchGuids.length > 1 ? 'es' : ''} ${getRanges(
          batchGuids.map((batchGuid) => batches[batchGuid]?.id).filter(notNullish),
        )}`
      : '';
  const bundlesString =
    bundleGuids.length > 0
      ? `bundle${bundleGuids.length > 1 ? 's' : ''} ${getRanges(
          bundleGuids.map((bundleGuid) => bundles[bundleGuid]?.id).filter(notNullish),
        )}`
      : '';

  return (
    <>
      <Box>
        <TooltipIconButton
          title="Remove from production group"
          size="small"
          onClick={() =>
            isPartOfBatchesOrBundles
              ? void setConfirmDeleteOpen(true)
              : void dispatch(
                  removeProductionComponentInstancesFromProductionGroup(
                    productionComponentInstanceGuidsForAnalysisSetGuid,
                  ),
                )
          }
        >
          <DeleteIcon />
        </TooltipIconButton>
      </Box>
      <Dialog
        open={confirmDeleteOpen}
        title="Confirm delete"
        onSubmit={async () => {
          setConfirmDeleteOpen(false);
          if (batchGuids.length > 0)
            await dispatch(
              removeProductionComponentInstancesFromBatch(productionComponentInstanceGuidsForAnalysisSetGuid),
            );
          if (bundleGuids.length > 0)
            await dispatch(
              removeProductionComponentInstancesFromBundle(productionComponentInstanceGuidsForAnalysisSetGuid),
            );
          dispatch(
            removeProductionComponentInstancesFromProductionGroup(productionComponentInstanceGuidsForAnalysisSetGuid),
          );
        }}
        onCancel={() => void setConfirmDeleteOpen(false)}
        enterKeySubmits
      >
        <DialogContentText>{`This truss is part of ${batchesString}${
          batchGuids.length > 0 && bundleGuids.length > 0 ? ' and ' : ''
        }${bundlesString}. Removing it from this production group will remove it there as well. Proceed?`}</DialogContentText>
      </Dialog>
    </>
  );
}
